import type { FlowDocument } from '@inference.engine/agentic';
import type { ModelTier } from '@inference.engine/model-registry/web';

export type DefinitionScope = 'org' | 'workspace' | 'user';

// eslint-disable-next-line i18next/no-literal-string -- Tools hub category ids
export const AGENT_CATEGORIES = ['plan', 'create', 'scale', 'deliver', 'support'] as const;
export type AgentCategory = (typeof AGENT_CATEGORIES)[number];

export interface AgentDefinitionMetadata {
  name: string;
  /** The Tools hub category it is listed under; without one it is listed in none. */
  tag?: AgentCategory;
  description?: string;
  greeting?: string;
  placeholderPrompt?: string;
  capabilities?: AgentCapabilities;
  /** The chat offers replies to pick after each answer. */
  suggestions?: boolean;
}

/** What the chat offers beside the message box for this agent. */
export interface AgentCapabilities {
  knowledgeBases?: boolean;
  guidelines?: boolean;
  /** The model tiers a person may pick from; with none, the agent answers with its own model. */
  modelTiers?: ModelTier[];
  /** Models picked one by one, before tiers; read only while `modelTiers` is absent. */
  supportedModels?: Array<{ key: string; provider: string }>;
}

/** What a definition's draft and each of its versions hold. */
export interface AgentDefinitionDocument {
  flow: FlowDocument;
  metadata: AgentDefinitionMetadata;
}

export interface DefinitionIssue {
  code: string;
  message: string;
  kind?: string;
  id?: string;
  pointer?: string;
  /** For a knowledge base the scope cannot reach: the workspaces it reaches that are not shared with it. */
  workspaceIds?: string[];
  /** For a widget the person can see. */
  widgetName?: string;
}

/** A definition's agent id as the chat and the engine know it. */
export const AGENT_DEFINITION_ID_PATTERN = /^adef_[0-9a-f]{32}(?:__[0-9a-f]{8})?$/;

export const isAgentDefinitionAgentId = (agentId: string | null | undefined): boolean =>
  typeof agentId === 'string' && AGENT_DEFINITION_ID_PATTERN.test(agentId);
